/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    useState,
    useCallback,
    useEffect,
    type ComponentPropsWithoutRef,
    type CSSProperties,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import { useLocation } from 'react-router';
import { CategoryNavLink } from '@/components/category-nav-link';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { NavigationSeoLinks } from '@/components/navigation-menu/seo-links';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { useHeaderPanel } from '@/components/header/use-header-panel';
import { DeferredMobileMenuDropdown } from './deferred-mobile-menu-dropdown';
import { NavigationMenuContext, useNavigationMenu } from './menu-context';

// eslint-disable-next-line react-refresh/only-export-components -- public hook API for consumers
export { useNavigationMenu } from './menu-context';
export { DeferredMobileMenuDropdown } from './deferred-mobile-menu-dropdown';

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <CategoryNavLink category={category} {...props}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </CategoryNavLink>
        </NavigationMenuLink>
    );
}

/** Max L2 columns per row in a vertical mega-menu panel; additional categories wrap. */
const MEGA_MENU_MAX_COLUMNS = 6;

/** Gutters between L2 columns — keep in sync with `--mega-menu-gap` on the parent `ul`. */
const MEGA_MENU_GAP = '1rem';

/** L2 column slot width — reads `--mega-menu-max-columns` and `--mega-menu-gap` from the parent `ul`. */
const MEGA_MENU_COLUMN_CLASS =
    'min-w-0 shrink grow-0 basis-[calc((100%-(var(--mega-menu-max-columns)-1)*var(--mega-menu-gap))/var(--mega-menu-max-columns))] max-w-[calc((100%-(var(--mega-menu-max-columns)-1)*var(--mega-menu-gap))/var(--mega-menu-max-columns))]';

function useNavigationMenuController() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.search, location.hash]);

    // Top-level branch triggers are real links (CategoryNavigationMenuBranchTrigger), so mouse
    // clicks navigate natively — no programmatic navigation handler needed here.
    const getElementProps = useCallback(
        ({ level }: { level: number; category: ShopperProducts.schemas['Category']; isLeaf?: boolean }) => {
            if (level < 1) {
                return { className: 'text-sm font-medium' };
            }

            return {
                className: cn(
                    '!px-2 !py-1',
                    level === 1 && 'text-sm font-semibold',
                    level >= 2 && 'text-xs font-medium'
                ),
            };
        },
        []
    );

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    return {
        mobileMenuOpen,
        setMobileMenuOpen,
        getElementProps,
        defaultListStyle,
    };
}

export function NavigationMenuDesktop(): ReactElement {
    // Root categories only — dropdown panels resolve enriched subcategory data per item via the
    // SubCategoryStore (see CategoryNavigationMenuNested), so no full-menu re-render on defer.
    const { categories, getElementProps, defaultListStyle } = useNavigationMenu();
    const headerPanel = useHeaderPanel();
    const isDesktop = useIsDesktop();
    const [openCategoryId, setOpenCategoryId] = useState('');
    const isMenuOpen = openCategoryId !== '';
    const setPanelOpen = headerPanel?.setPanelOpen;
    const registerPanelCloseHandler = headerPanel?.registerPanelCloseHandler;

    // Shared header backdrop is for desktop mega-menu only — mobile Sheet has its own overlay.
    useEffect(() => {
        setPanelOpen?.('menu', isDesktop && isMenuOpen);
    }, [isDesktop, isMenuOpen, setPanelOpen]);

    useEffect(() => {
        if (!isDesktop && openCategoryId !== '') {
            setOpenCategoryId('');
        }
    }, [isDesktop, openCategoryId]);

    useEffect(() => {
        registerPanelCloseHandler?.('menu', () => setOpenCategoryId(''));
        return () => registerPanelCloseHandler?.('menu', null);
    }, [registerPanelCloseHandler]);

    return (
        <div className="flex items-center h-full">
            <CategoryNavigationMenu
                categories={categories}
                openCategoryId={openCategoryId}
                onOpenChange={setOpenCategoryId}
                delayDuration={0}
                interactionLocked={headerPanel?.isSearchSuggestionsOpen ?? false}
                propsViewport={() => ({
                    className: 'rounded-none border-0 shadow-lg overscroll-contain overflow-y-auto',
                    style: {
                        position: 'fixed',
                        top: 'calc(var(--ticker-height, 0px) + var(--header-height, 0px))',
                        left: 0,
                        right: 0,
                        maxHeight: 'calc(100dvh - var(--ticker-height, 0px) - var(--header-height, 0px))',
                    },
                })}
                propsContentContainer={() => ({
                    className: '!p-0 !left-auto !right-auto !w-full lg:!w-full',
                })}
                propsContent={({ category }) => ({
                    className: cn(
                        'section-container pt-4 px-16 pb-12',
                        hasBanner(category) &&
                            (isVertical(category)
                                ? 'grid md:grid-cols-[1fr_.3fr] items-start'
                                : 'grid md:grid-cols-[1fr_.6fr] items-start')
                    ),
                })}
                propsList={({ parent, categories: subCategories, level }) => {
                    if (level === 1) {
                        if (isVertical(parent)) {
                            return {
                                style: {
                                    ...defaultListStyle,
                                    '--mega-menu-max-columns': MEGA_MENU_MAX_COLUMNS,
                                    '--mega-menu-gap': MEGA_MENU_GAP,
                                } as CSSProperties,
                                className:
                                    'flex w-full flex-row flex-wrap justify-start gap-[var(--mega-menu-gap)] p-0',
                            };
                        }
                        return {
                            style: {
                                ...defaultListStyle,
                                gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                            },
                            className: 'grid p-0',
                        };
                    }
                }}
                propsListItem={({ level }) => {
                    if (level === 1) {
                        return { className: MEGA_MENU_COLUMN_CLASS };
                    }
                }}
                propsElement={getElementProps}
                renderSlotListAfter={({ level, parent }) => {
                    if (level === 1 && hasBanner(parent)) {
                        return (
                            <aside className="self-stretch">
                                <CategoryBanner category={parent} />
                            </aside>
                        );
                    }
                }}
            />
        </div>
    );
}

export function NavigationMenuMobileToggle(): ReactElement {
    const { isOpen, toggle, close } = useNavigationMenu();
    const { t } = useTranslation('header');
    const headerPanel = useHeaderPanel();
    const searchLocked = headerPanel?.isSearchSuggestionsOpen ?? false;

    useEffect(() => {
        if (searchLocked) {
            close();
        }
    }, [searchLocked, close]);

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={searchLocked ? undefined : toggle}
            disabled={searchLocked}
            // Stay mounted (never `return null`) while the Sheet is open: unmounting would
            // collapse this button's flex slot — shifting the search bar visible through the
            // Sheet's translucent overlay — and would break Radix's focus-restore, which needs
            // this exact DOM node to still exist when the Sheet closes.
            className={cn('hover:bg-transparent hover:opacity-50 transition-opacity', isOpen && 'invisible')}
            tabIndex={isOpen ? -1 : undefined}
            aria-hidden={isOpen}
            aria-label={t('openMenu', 'Open menu')}
            aria-expanded={isOpen}>
            <Menu className="size-5" />
        </Button>
    );
}

export function NavigationMenuProvider({
    resolve,
    defer,
    children,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> & PropsWithChildren): ReactElement {
    const controller = useNavigationMenuController();

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer}>
            {({ categories }) => (
                <NavigationMenuContext.Provider
                    value={{
                        categories,
                        isOpen: controller.mobileMenuOpen,
                        toggle: () => controller.setMobileMenuOpen((open) => !open),
                        close: () => controller.setMobileMenuOpen(false),
                        getElementProps: controller.getElementProps,
                        defaultListStyle: controller.defaultListStyle,
                    }}>
                    {children}
                    {/* Streams nested crawl links from the same defer promise the store prefetches in useEffect. */}
                    <NavigationSeoLinks defer={defer} />
                </NavigationMenuContext.Provider>
            )}
        </WithCategoryNavigationMenu>
    );
}

/**
 * ResponsiveNavigationMenu — header navigation wiring for Storybook and standalone use.
 *
 * Composes three complementary pieces (not a single shared DOM tree):
 * - Desktop: {@link NavigationMenuDesktop} — Radix mega menu (lg+)
 * - Mobile: hamburger + deferred Sheet drawer with L1 carousel tabs and L2 accordions (< lg)
 * - SEO: {@link NavigationSeoLinks} — sr-only flat links streamed from `defer` (via {@link NavigationMenuProvider})
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data (store prefetch + SEO link stream)
 * @returns Responsive header navigation for mobile and desktop viewports
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu>): ReactElement {
    return (
        <NavigationMenuProvider resolve={resolve} defer={defer}>
            <div className="lg:hidden">
                <NavigationMenuMobileToggle />
            </div>
            <div className="hidden lg:flex">
                <NavigationMenuDesktop />
            </div>
            <DeferredMobileMenuDropdown />
        </NavigationMenuProvider>
    );
}
